import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Loader2, TrendingUp, TrendingDown, DollarSign, ShoppingCart, AlertTriangle, ExternalLink } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from "recharts";
import api from "../../lib/api";

const WEEKDAYS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const formatTrendLabel = (raw) => {
  if (!raw || !/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw;
  const d = new Date(raw + "T00:00:00");
  if (Number.isNaN(d.getTime())) return raw;
  return `${WEEKDAYS_ES[d.getDay()]}, ${raw.slice(5)}`;
};

const KpiCard = ({ title, value, prev, trend, icon: Icon, format = "€", testId }) => {
  const up = trend >= 0;
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold mt-1">{format === "€" ? `${value.toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}€` : value.toLocaleString("es-ES")}</p>
            {prev !== undefined && (
              <div className={`flex items-center gap-1 mt-1 text-xs ${up ? "text-green-600" : "text-red-500"}`}>
                {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                <span>{up ? "+" : ""}{trend}% vs período anterior</span>
              </div>
            )}
          </div>
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AnomalyDot = ({ cx, cy, payload, anomalies }) => {
  const isAnomaly = anomalies.some(a => a.date === payload.date);
  if (!isAnomaly) return null;
  const a = anomalies.find(a => a.date === payload.date);
  return (
    <circle cx={cx} cy={cy} r={5} fill={a?.type === "spike" ? "#16a34a" : "#dc2626"} stroke="white" strokeWidth={2} />
  );
};

export function OverviewTab({ restaurantId, startDate, endDate, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    api.request(`/restaurants/${restaurantId}/analytics/overview?${new URLSearchParams({ ...(startDate && { start_date: startDate }), ...(endDate && { end_date: endDate }) })}`)
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [restaurantId, startDate, endDate]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!data) return null;

  const { kpis, daily_trend, anomalies } = data;

  return (
    <div className="space-y-6">
      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Ventas totales" value={kpis.total} prev={kpis.prev_total} trend={kpis.trend_pct} icon={DollarSign} testId="kpi-total" />
        <KpiCard title="Transacciones" value={kpis.transactions} prev={kpis.prev_transactions} trend={kpis.tx_trend} icon={ShoppingCart} format="num" testId="kpi-tx" />
        <KpiCard title="Ticket medio" value={kpis.avg_ticket} icon={DollarSign} testId="kpi-avg" />
        <KpiCard title="Beneficio neto" value={kpis.net_profit} icon={TrendingUp} testId="kpi-net" />
      </div>

      {/* Trend chart */}
      <Card data-testid="overview-trend-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center justify-between">
            Tendencia de ventas
            {anomalies.length > 0 && (
              <Badge variant="outline" className="text-xs gap-1">
                <AlertTriangle className="w-3 h-3 text-amber-500" />
                {anomalies.length} anomalía{anomalies.length !== 1 ? "s" : ""}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={daily_trend} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="overviewTrendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={d => d.slice(5)} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `${v}€`} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(v) => [`${v}€`, "Ventas"]}
                labelFormatter={formatTrendLabel}
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "calc(var(--radius) - 2px)",
                  fontSize: 12,
                }}
                labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 600 }}
              />
              <Area
                type="monotone"
                dataKey="ventas"
                name="Ventas"
                stroke="hsl(var(--primary))"
                strokeWidth={2.5}
                strokeDasharray="6 4"
                fill="url(#overviewTrendFill)"
                activeDot={{ r: 4, fill: "hsl(var(--primary))", stroke: "hsl(var(--background))", strokeWidth: 2 }}
              />
              {anomalies.map(a => (
                <ReferenceDot key={a.date} x={a.date} y={a.ventas}
                  r={5} fill={a.type === "spike" ? "#16a34a" : "#dc2626"} stroke="white" strokeWidth={2}
                  label={{ value: `${a.pct > 0 ? "+" : ""}${a.pct}%`, fontSize: 9, fill: a.type === "spike" ? "#16a34a" : "#dc2626", dy: -10 }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Anomalies list */}
      {anomalies.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Anomalías detectadas</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {anomalies.map(a => (
                <div key={a.date} className="flex items-center justify-between p-2 rounded-lg bg-muted/40">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${a.type === "spike" ? "bg-green-500" : "bg-red-500"}`} />
                    <span className="text-sm font-medium">{a.date}</span>
                    <Badge variant="outline" className={`text-xs ${a.type === "spike" ? "text-green-600 border-green-200" : "text-red-700 border-red-200"}`}>
                      {a.type === "spike" ? "Pico" : "Caída"}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{a.ventas.toFixed(2)}€</p>
                    <p className={`text-xs ${a.pct > 0 ? "text-green-600" : "text-red-500"}`}>{a.pct > 0 ? "+" : ""}{a.pct}% de la media</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
